/**
 * Capture-plane HTTP client (forked from vCIP_droom_4.0/src/api/client.ts — same security posture).
 *
 *   • The link token is read ONCE from the URL and exchanged (POST /exchange) for a SIGNED,
 *     EXPIRING, case-scoped bearer that lives in memory only and rides as an Authorization
 *     header — never left in the address bar, Referer, storage, or logs.
 *   • Client-supplied geo/IMU are evidence INPUTS bound into the hash chain, never trusted
 *     headers; the server derives source IP / time drift itself.
 *   • MOCK mode is an EXPLICIT, FAIL-CLOSED opt-in (`VITE_MOCK=true`) — see transport.ts.
 *   • There is deliberately NO raw-token fallback: a refused exchange returns the typed refusal
 *     (410 SV_LINK_EXPIRED / 409 SV_CASE_NOT_CAPTURABLE) and the front door tells the
 *     representative honestly what state the request is in.
 */

import { SvNetworkError, toSvApiError } from './errors';
import { type TransportMethod, resolveTransport } from './transport';

export const CAPTURE_BASE = '/api/v1/sv/capture';

let bearer: string | null = null;
let bearerExpiresAtMs = 0;
let exchangeInFlight: Promise<ExchangeOutcome> | null = null;

/**
 * DEC-092 (contract v0.1.9 §D): the platform `TenantBranding` block the exchange summary carries — the SAME block
 * the session entry serves the data room. Every field may be null ("unset — platform default look"); nothing is
 * fabricated to fill a gap. Sanitised by theme/tenant.ts (real #hex, https logo) before anything renders it.
 */
export interface ServedBranding {
  readonly name: string | null;
  readonly brandColor: string | null;
  readonly brandColorStrong: string | null;
  readonly brandContrast: string | null;
  readonly logoUrl: string | null;
  readonly cobrand: string | null;
}

/** A DPDP notice the be issues with the exchange (SV-8-3). The pack's ratification STATUS is never on this wire. */
export interface SvNotice {
  /** CUSTOMER = the notice the representative consents to (the residence variant on a home); BYSTANDER_PROMPT = the
   *  on-screen workplace prompt before interior shots; AGENT = the field agent's own notice (its own purpose tag —
   *  never rendered on the representative's surface). */
  readonly role: 'CUSTOMER' | 'BYSTANDER_PROMPT' | 'AGENT';
  readonly noticeKey: string;
  readonly version: string;
  readonly noticeVersion: string;
  readonly locale: string;
  readonly controllingLocale: string;
  readonly purposeTag: string;
  readonly variant?: string;
  /** Markdown-ish body: `# title`, `## heading`, `- item`, paragraphs, `**bold**`. Already placeholder-substituted. */
  readonly body: string;
  readonly sha256: string;
}

/** The server's consent purpose vocabulary — the SERVER's keys and mandatory flags are the authority. */
export interface ConsentPurposeHint {
  readonly key: string;
  readonly mandatory: boolean;
  /** SV-5-2 second round: true on the three live-call purposes (LIVE_VIDEO, LIVE_IDENTITY_CHECK, LIVE_CUSTODY_RETENTION)
   *  the v2 notice adds when the live leg applies; absent/false on the seven base rows. */
  readonly liveLeg?: boolean;
}

/**
 * `summary.stamp` (contract v0.1.12 §2 + the DEC-095 addendum): the tenant's stamp election RESOLVED for this
 * case's premises kind, and the live-card mode. Read defensively by `capture/stampPreview.ts#parseStamp`;
 * the raw shape is kept here as the wire names it. Absent on an older be.
 */
export interface ServedStamp {
  readonly coordinates: 'SHOW' | 'HIDE' | string;
  /** DEC-095 C1 — absent on a be between v0.1.12 and the addendum ⇒ COORDINATES (the base default). */
  readonly liveCard?: 'COORDINATES' | 'HIDDEN' | string;
  readonly distanceBand: boolean;
  readonly plusCode: boolean;
  readonly timezone: string | null;
  readonly address: { readonly line: string } | null;
  readonly anchor: { readonly lat: number; readonly lng: number; readonly source: string; readonly confidence: string } | null;
}

export interface ExchangeSummary {
  readonly tenantName: string;
  readonly clientRef: string;
  /**
   * VS-02: an OPEN string — the tenant's pack names the kinds (India: OFFICE, SHOP, SHOPFLOOR, FACTORY, WAREHOUSE, FARM,
   * RESIDENCE, OTHER; Gulf: OFFICE, BUSINESS_CENTRE_DESK, WAREHOUSE, YARD, WORKSHOP, LABOUR_ACCOMMODATION, …). A kind the
   * app does not know is normal: print `premisesKindLabel` (see `flow/premisesKind.ts`).
   */
  readonly premisesKind: string;
  /** VS-02 (be with a v2 taxonomy): the pack's label for the kind, in the representative's language. Absent on India cases. */
  readonly premisesKindLabel?: string | null;
  /** VS-02: the kind's privacy floor — DWELLING takes the dwelling guidance (no rooms, no residents). Absent on India cases. */
  readonly premisesPrivacyClass?: 'WORKPLACE' | 'DWELLING' | 'MOBILE_ASSET' | null;
  readonly purpose: string;
  readonly captureList: readonly string[];
  /**
   * VC-12 r5 (contract §13): parallel to `captureList` — row i is `{locale: text}` for `captureList[i]`, in the pack's
   * languages only (`en` IS the `captureList` row). Present ONLY when the case's pack declares languages; absent on every
   * India case. A row without the customer's language falls back to `captureList[i]` (see `flow/captureList.ts`).
   */
  readonly captureListI18n?: readonly Readonly<Record<string, string>>[] | null;
  /** VC-12 r5: `premisesKindLabel` in each pack language, so the door follows the customer's choice. Absent on India cases. */
  readonly premisesKindLabelI18n?: Readonly<Record<string, string>> | null;
  readonly verificationType: 'REMOTE_GUIDED' | 'FIELD_ATTENDED' | 'REMOTE_LIVE_OFFICER';
  readonly locale: string;
  readonly noticeVersion: string;
  readonly otpRequired: false;
  /** Present on a be ≥ contract v0.1.9 (DEC-092); absent on an older be ⇒ the neutral platform theme. */
  readonly branding?: ServedBranding | null;
  /** Present on a be ≥ V157 (the pack's consent_purposes); absent on an older be ⇒ the client's legacy list. */
  readonly consentPurposes?: readonly ConsentPurposeHint[];
  /** Present on a be ≥ V167 (SV-8-3); absent ⇒ the deck's summary texts only. */
  readonly notices?: readonly SvNotice[];
  /** Present on a be ≥ V177 (contract v0.1.12); absent ⇒ the no-anchor card form, never a coordinate from the server. */
  readonly stamp?: ServedStamp | null;
  /**
   * SPEC_CAPTURE_UX_R2 §2.2 (be V178): the language the CUSTOMER chose on the chooser (`PATCH /locale`), case-scoped so a
   * link re-opened on another phone restores it without asking again; null until chosen. Distinct from `locale`, the
   * tenant-declared preference the chooser only puts first.
   */
  readonly customerLocale?: string | null;
  /**
   * VC-12 (be V223): the languages the case's pack offers the customer journey, in pack order — the Gulf packs say
   * `[en, ar]`. Present ONLY when the pack declares languages; absent on every India case. A pack-gated language (Arabic)
   * is offered only when it is listed here (`i18n/runtime.ts#offerableLocales`).
   */
  readonly packLocales?: readonly string[] | null;
}

export interface ExchangeResult {
  readonly bearer: string;
  readonly expiresAt: string;
  readonly summary: ExchangeSummary;
}

/**
 * SPEC-15 / S15-1 (contract v0.1.9 §C): when the tenant's `sv.link_otp` is EMAIL or SMS the exchange answers
 * 200 with THIS and NO bearer — the code went to the representative's invited email / mobile on the case.
 * `POST /otp/verify {otpId, code}` then returns the full ExchangeResult. The link is the first credential, the
 * code is the second; neither is ever stored on this device.
 */
export interface OtpChallenge {
  readonly otpRequired: true;
  readonly channel: 'EMAIL' | 'SMS';
  /** Already masked by the server — `r***@x.com` / `+91 ******1234`; rendered verbatim, never un-masked here. */
  readonly maskedTarget: string;
  readonly otpId: string;
  /** PWA lane pin 1 (be #159): the tenant's name and the public-safe branding block; absent on an older be. */
  readonly tenantName?: string | null;
  readonly branding?: ServedBranding | null;
  /**
   * VC-12 r5 (contract §10.C as amended): the case pack's languages, so the code screen can offer Arabic BEFORE any bearer
   * exists. OMITTED when the pack declares none — every India challenge is byte-identical to the pre-VC-12 wire.
   */
  readonly packLocales?: readonly string[] | null;
}

export type ExchangeOutcome = ExchangeResult | OtpChallenge;

export function isOtpChallenge(o: ExchangeOutcome): o is OtpChallenge {
  return (o as { otpRequired?: unknown }).otpRequired === true;
}

/** Memory-only. Tests and the resume path may seed it; nothing persists it. */
export function setBearer(token: string | null, expiresAt?: string): void {
  bearer = token;
  bearerExpiresAtMs = expiresAt ? Date.parse(expiresAt) || 0 : 0;
}

export function getBearer(): string | null {
  return bearer;
}

/** True when the bearer is within 5 minutes of its stated expiry (or already past it). */
export function bearerNearDeath(): boolean {
  return bearerExpiresAtMs > 0 && Date.now() > bearerExpiresAtMs - 5 * 60 * 1000;
}

/**
 * POST /exchange — the front door. Concurrent callers share one in-flight exchange (the resume
 * path can fire twice). Throws `SvApiError` on a typed refusal, `SvNetworkError` when unreachable.
 * Resolves to EITHER the exchange (bearer set) OR an OtpChallenge (no bearer — the caller must run the OTP
 * screen and call `verifyLinkOtp`); callers that cannot show a screen treat a challenge as "not exchanged".
 */
export async function exchangeLinkToken(linkToken: string): Promise<ExchangeOutcome> {
  if (exchangeInFlight) return exchangeInFlight;
  exchangeInFlight = (async () => {
    try {
      const transport = await resolveTransport();
      let res;
      try {
        res = await transport.send({
          method: 'POST',
          path: `${CAPTURE_BASE}/exchange`,
          headers: {},
          body: { linkToken },
        });
      } catch (e) {
        throw new SvNetworkError('exchange unreachable', e);
      }
      if (res.status < 200 || res.status >= 300) {
        throw toSvApiError(res.status, res.body, `POST /exchange → ${res.status}`);
      }
      const body = res.body as ExchangeOutcome;
      if (isOtpChallenge(body)) return body;
      setBearer(body.bearer, body.expiresAt);
      return body;
    } finally {
      exchangeInFlight = null;
    }
  })();
  return exchangeInFlight;
}

/**
 * POST /otp/verify {otpId, code} → the full exchange payload (bearer, expiresAt, summary…). Unauthenticated
 * (there is no bearer yet). A wrong code is a typed 4xx (attempts-left in `details`); the fifth wrong code
 * locks the LINK (a re-invite unlocks it); an expired code is a 4xx of its own. The code string is sent
 * once and never kept.
 */
export async function verifyLinkOtp(otpId: string, code: string): Promise<ExchangeResult> {
  const transport = await resolveTransport();
  let res;
  try {
    res = await transport.send({
      method: 'POST',
      path: `${CAPTURE_BASE}/otp/verify`,
      headers: {},
      body: { otpId, code },
    });
  } catch (e) {
    throw new SvNetworkError('otp verify unreachable', e);
  }
  if (res.status < 200 || res.status >= 300) {
    throw toSvApiError(res.status, res.body, `POST /otp/verify → ${res.status}`);
  }
  const body = res.body as ExchangeResult;
  setBearer(body.bearer, body.expiresAt);
  return body;
}

export interface RequestOptions {
  readonly method?: TransportMethod;
  readonly body?: unknown | Uint8Array;
  readonly headers?: Readonly<Record<string, string>>;
}

/**
 * Authenticated request. 2xx → parsed body (null on 204); anything else throws the typed error.
 * If an exchange is in flight, waits for it before building headers (the droom's 18-Aug race belt).
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (exchangeInFlight) {
    try {
      await exchangeInFlight;
    } catch {
      /* the caller's own request reports the refusal */
    }
  }
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
  const transport = await resolveTransport();
  let res;
  try {
    res = await transport.send({
      method: opts.method ?? 'GET',
      path,
      headers,
      ...(opts.body !== undefined ? { body: opts.body } : {}),
    });
  } catch (e) {
    throw new SvNetworkError(`${opts.method ?? 'GET'} ${path} unreachable`, e);
  }
  if (res.status < 200 || res.status >= 300) {
    throw toSvApiError(res.status, res.body, `${opts.method ?? 'GET'} ${path} → ${res.status}`, res.retryAfterS ?? null);
  }
  return res.body as T;
}

/** Test-only reset of the module state. */
export function resetClientForTests(): void {
  bearer = null;
  bearerExpiresAtMs = 0;
  exchangeInFlight = null;
}
